// Synth drum kit — kick/tom (MembraneSynth), snare/clap/hat/crash (NoiseSynth).
// Hat and crash are bright high-passed noise bursts: MetalSynth produced no
// audible output at any volume here, so filtered noise is the reliable cymbal.
// Not scale-locked; `note` carries the voice id.

import * as Tone from "tone";
import { clampVelocity, type Instrument } from "./types.ts";

export type DrumVoice = "kick" | "snare" | "hat" | "tom" | "clap" | "crash";

export function createDrumKit(): Instrument {
  let bus: Tone.Gain | null = null;
  let kick: Tone.MembraneSynth | null = null;
  let tom: Tone.MembraneSynth | null = null;
  let snare: Tone.NoiseSynth | null = null;
  let clap: Tone.NoiseSynth | null = null;
  let hat: Tone.NoiseSynth | null = null;
  let hatHP: Tone.Filter | null = null;
  let crash: Tone.NoiseSynth | null = null;
  let crashHP: Tone.Filter | null = null;

  return {
    id: "drum-kit",
    name: "Drum kit",
    category: "drums",
    archetype: "air-drum",
    scaleLocked: false,
    polyphony: 6,
    init(output) {
      bus = new Tone.Gain().connect(output);

      kick = new Tone.MembraneSynth({
        pitchDecay: 0.03,
        octaves: 6,
        envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0.1 },
      }).connect(bus);

      tom = new Tone.MembraneSynth({
        pitchDecay: 0.02,
        octaves: 3,
        envelope: { attack: 0.001, decay: 0.25, sustain: 0, release: 0.1 },
      }).connect(bus);

      snare = new Tone.NoiseSynth({
        noise: { type: "white" },
        envelope: { attack: 0.001, decay: 0.18, sustain: 0 },
      }).connect(bus);
      snare.volume.value = -8;

      clap = new Tone.NoiseSynth({
        noise: { type: "pink" },
        envelope: { attack: 0.002, decay: 0.13, sustain: 0 },
      }).connect(bus);
      clap.volume.value = -10;

      hatHP = new Tone.Filter({ type: "highpass", frequency: 8000 }).connect(bus);
      hat = new Tone.NoiseSynth({
        noise: { type: "white" },
        envelope: { attack: 0.001, decay: 0.05, sustain: 0 },
      }).connect(hatHP);
      hat.volume.value = -6;

      crashHP = new Tone.Filter({ type: "highpass", frequency: 5000 }).connect(bus);
      crash = new Tone.NoiseSynth({
        noise: { type: "white" },
        envelope: { attack: 0.001, decay: 1.0, sustain: 0, release: 0.4 },
      }).connect(crashHP);
      crash.volume.value = -12;
    },
    trigger(ev, time) {
      const v = clampVelocity(ev.velocity);
      const when = time ?? Tone.now(); // live play passes no time → now
      switch (ev.note as DrumVoice) {
        case "kick":
          kick?.triggerAttackRelease("C1", "8n", when, v);
          break;
        case "tom":
          tom?.triggerAttackRelease("A2", "8n", when, v);
          break;
        case "snare":
          snare?.triggerAttackRelease("8n", when, v);
          break;
        case "clap":
          clap?.triggerAttackRelease("8n", when, v);
          break;
        case "hat":
          hat?.triggerAttackRelease("16n", when, v);
          break;
        case "crash":
          crash?.triggerAttackRelease("1n", when, v);
          break;
      }
    },
    dispose() {
      for (const node of [kick, tom, snare, clap, hat, hatHP, crash, crashHP, bus]) node?.dispose();
      bus = kick = tom = snare = clap = null;
      hat = crash = null;
      hatHP = crashHP = null;
    },
  };
}
